import type { RealtimeChannel } from '@supabase/supabase-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabase } from '../lib/supabase';
import type { Profile } from '../types';

export type CallMode = 'voice' | 'video';

export interface RemoteCallParticipant {
  id: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
  stream: MediaStream | null;
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
}

export interface IncomingCall {
  id: string;
  from: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
  mode: CallMode;
}

export interface CallReaction {
  id: string;
  emoji: string;
  from: string;
  displayName: string;
}

export interface RealtimeCall {
  active: boolean;
  mode: CallMode | null;
  status: string;
  error: string | null;
  incomingCall: IncomingCall | null;
  localStream: MediaStream | null;
  remoteParticipants: RemoteCallParticipant[];
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  reactions: CallReaction[];
  startCall: (mode: CallMode) => Promise<void>;
  joinIncomingCall: () => Promise<void>;
  leaveCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => Promise<void>;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  sendReaction: (emoji: string) => void;
  dismissIncomingCall: () => void;
  clearError: () => void;
}

type ParticipantMeta = Omit<RemoteCallParticipant, 'id' | 'stream'>;

interface BaseSignal {
  from: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
}

interface JoinSignal extends BaseSignal {
  callId: string;
  mode: CallMode;
}

interface SessionSignal extends BaseSignal {
  to: string;
  mode: CallMode;
  sdp: RTCSessionDescriptionInit;
}

interface IceSignal {
  from: string;
  to: string;
  candidate: RTCIceCandidateInit;
}

interface LeaveSignal {
  from: string;
}

interface ReactionSignal {
  id: string;
  emoji: string;
  from: string;
  displayName: string;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

function createSignalId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isLive(track: MediaStreamTrack | null | undefined) {
  return Boolean(track && track.readyState === 'live');
}

export function useRealtimeCall(conversationId: string | null, profile: Profile): RealtimeCall {
  const profileRef = useRef(profile);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const videoSendersRef = useRef<Map<string, RTCRtpSender>>(new Map());
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const participantMetaRef = useRef<Map<string, ParticipantMeta>>(new Map());
  const callIdRef = useRef<string | null>(null);
  const activeRef = useRef(false);
  const audioEnabledRef = useRef(true);
  const videoEnabledRef = useRef(false);
  const screenSharingRef = useRef(false);
  const modeRef = useRef<CallMode | null>(null);

  const [active, setActive] = useState(false);
  const [mode, setMode] = useState<CallMode | null>(null);
  const [status, setStatus] = useState('Call channel ready');
  const [error, setError] = useState<string | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteCallParticipant[]>([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [reactions, setReactions] = useState<CallReaction[]>([]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const setActiveValue = useCallback((value: boolean) => {
    activeRef.current = value;
    setActive(value);
  }, []);

  const setModeValue = useCallback((value: CallMode | null) => {
    modeRef.current = value;
    setMode(value);
  }, []);

  const setAudioEnabledValue = useCallback((value: boolean) => {
    audioEnabledRef.current = value;
    setAudioEnabled(value);
  }, []);

  const setVideoEnabledValue = useCallback((value: boolean) => {
    videoEnabledRef.current = value;
    setVideoEnabled(value);
  }, []);

  const setScreenSharingValue = useCallback((value: boolean) => {
    screenSharingRef.current = value;
    setScreenSharing(value);
  }, []);

  const setLocalMediaStream = useCallback((stream: MediaStream | null) => {
    localStreamRef.current = stream;
    setLocalStream(stream);
  }, []);

  const publishRemoteParticipants = useCallback(() => {
    setRemoteParticipants(
      Array.from(participantMetaRef.current.entries()).map(([id, meta]) => ({
        id,
        ...meta,
        stream: remoteStreamsRef.current.get(id) ?? null,
      })),
    );
  }, []);

  const sendSignal = useCallback((event: string, payload: unknown) => {
    void channelRef.current?.send({
      type: 'broadcast',
      event,
      payload,
    });
  }, []);

  const buildBaseSignal = useCallback((): BaseSignal => {
    const currentProfile = profileRef.current;

    return {
      from: currentProfile.id,
      displayName: currentProfile.display_name,
      username: currentProfile.username,
      avatarUrl: currentProfile.avatar_url,
      audioEnabled: audioEnabledRef.current,
      videoEnabled: videoEnabledRef.current,
      screenSharing: screenSharingRef.current,
    };
  }, []);

  const updateParticipantMeta = useCallback((id: string, meta: Partial<ParticipantMeta> & Pick<ParticipantMeta, 'displayName'>) => {
    const existing = participantMetaRef.current.get(id);
    participantMetaRef.current.set(id, {
      username: meta.username ?? existing?.username ?? 'relay-user',
      displayName: meta.displayName,
      avatarUrl: meta.avatarUrl ?? existing?.avatarUrl ?? null,
      audioEnabled: meta.audioEnabled ?? existing?.audioEnabled ?? true,
      videoEnabled: meta.videoEnabled ?? existing?.videoEnabled ?? false,
      screenSharing: meta.screenSharing ?? existing?.screenSharing ?? false,
    });
    publishRemoteParticipants();
  }, [publishRemoteParticipants]);

  const closePeer = useCallback(
    (peerId: string) => {
      const connection = peerConnectionsRef.current.get(peerId);
      connection?.getSenders().forEach((sender) => {
        if (sender.track?.kind === 'video') {
          videoSendersRef.current.delete(peerId);
        }
      });
      connection?.close();
      peerConnectionsRef.current.delete(peerId);
      videoSendersRef.current.delete(peerId);
      remoteStreamsRef.current.delete(peerId);
      participantMetaRef.current.delete(peerId);
      publishRemoteParticipants();
    },
    [publishRemoteParticipants],
  );

  const createPeerConnection = useCallback(
    (peerId: string) => {
      const existing = peerConnectionsRef.current.get(peerId);
      if (existing) return existing;

      const connection = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      const currentStream = localStreamRef.current;
      let hasAudioSender = false;
      let hasVideoSender = false;

      currentStream?.getTracks().forEach((track) => {
        const sender = connection.addTrack(track, currentStream);
        if (track.kind === 'audio') hasAudioSender = true;
        if (track.kind === 'video') {
          hasVideoSender = true;
          videoSendersRef.current.set(peerId, sender);
        }
      });

      if (!hasAudioSender) {
        connection.addTransceiver('audio', { direction: 'sendrecv' });
      }

      if (!hasVideoSender) {
        const transceiver = connection.addTransceiver('video', { direction: 'sendrecv' });
        videoSendersRef.current.set(peerId, transceiver.sender);
      }

      connection.onicecandidate = (event) => {
        if (!event.candidate) return;
        sendSignal('ice-candidate', {
          from: profileRef.current.id,
          to: peerId,
          candidate: event.candidate.toJSON(),
        } satisfies IceSignal);
      };

      connection.ontrack = (event) => {
        const remoteStream = remoteStreamsRef.current.get(peerId) ?? new MediaStream();
        const sourceStream = event.streams[0];

        if (sourceStream) {
          sourceStream.getTracks().forEach((track) => {
            if (!remoteStream.getTracks().some((currentTrack) => currentTrack.id === track.id)) {
              remoteStream.addTrack(track);
            }
          });
        } else if (event.track && !remoteStream.getTracks().some((track) => track.id === event.track.id)) {
          remoteStream.addTrack(event.track);
        }

        remoteStreamsRef.current.set(peerId, remoteStream);
        publishRemoteParticipants();
      };

      connection.onconnectionstatechange = () => {
        if (connection.connectionState === 'connected') {
          setStatus('Call connected');
        }

        if (connection.connectionState === 'failed' || connection.connectionState === 'closed') {
          closePeer(peerId);
        }
      };

      peerConnectionsRef.current.set(peerId, connection);
      return connection;
    },
    [closePeer, publishRemoteParticipants, sendSignal],
  );

  const replaceVideoTrackForPeers = useCallback((track: MediaStreamTrack | null) => {
    videoSendersRef.current.forEach((sender) => {
      void sender.replaceTrack(track);
    });
  }, []);

  const broadcastMediaState = useCallback(() => {
    if (!activeRef.current) return;
    sendSignal('media-state', buildBaseSignal());
  }, [buildBaseSignal, sendSignal]);

  const stopScreenShare = useCallback(() => {
    const screenTrack = screenStreamRef.current?.getVideoTracks()[0];
    screenStreamRef.current?.getTracks().forEach((track) => track.stop());
    screenStreamRef.current = null;

    const cameraTrack = isLive(cameraTrackRef.current) ? cameraTrackRef.current : null;
    const audioTracks = localStreamRef.current?.getAudioTracks().filter(isLive) ?? [];
    const nextStream = new MediaStream([...audioTracks, ...(cameraTrack && videoEnabledRef.current ? [cameraTrack] : [])]);

    setLocalMediaStream(nextStream);
    replaceVideoTrackForPeers(cameraTrack && videoEnabledRef.current ? cameraTrack : null);
    setScreenSharingValue(false);
    setStatus(screenTrack ? 'Screen share stopped' : status);
    broadcastMediaState();
  }, [broadcastMediaState, replaceVideoTrackForPeers, setLocalMediaStream, setScreenSharingValue, status]);

  const stopAllLocalMedia = useCallback(() => {
    const stopped = new Set<string>();
    const stopTrack = (track: MediaStreamTrack | null | undefined) => {
      if (!track || stopped.has(track.id)) return;
      stopped.add(track.id);
      track.stop();
    };

    localStreamRef.current?.getTracks().forEach(stopTrack);
    screenStreamRef.current?.getTracks().forEach(stopTrack);
    stopTrack(cameraTrackRef.current);

    screenStreamRef.current = null;
    cameraTrackRef.current = null;
    setLocalMediaStream(null);
  }, [setLocalMediaStream]);

  const ensureLocalStream = useCallback(
    async (nextMode: CallMode) => {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Your browser does not support microphone and camera access.');
      }

      const existing = localStreamRef.current;
      const hasLiveAudio = existing?.getAudioTracks().some(isLive) ?? false;
      const hasLiveVideo = existing?.getVideoTracks().some(isLive) ?? false;

      if (existing && hasLiveAudio && (nextMode === 'voice' || hasLiveVideo)) {
        return existing;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: nextMode === 'video',
      });

      stream.getAudioTracks().forEach((track) => {
        track.enabled = audioEnabledRef.current;
      });

      stopAllLocalMedia();

      stream.getVideoTracks().forEach((track) => {
        track.enabled = videoEnabledRef.current;
        cameraTrackRef.current = track;
      });

      setLocalMediaStream(stream);
      return stream;
    },
    [setLocalMediaStream, stopAllLocalMedia],
  );

  const enableCameraTrack = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Your browser does not support camera access.');
    }

    const existingVideo = localStreamRef.current?.getVideoTracks().find(isLive);
    if (existingVideo && !screenSharingRef.current) {
      existingVideo.enabled = true;
      setVideoEnabledValue(true);
      replaceVideoTrackForPeers(existingVideo);
      broadcastMediaState();
      return existingVideo;
    }

    const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    const [cameraTrack] = cameraStream.getVideoTracks();

    if (!cameraTrack) {
      throw new Error('No camera track was returned by the browser.');
    }

    cameraTrack.enabled = true;
    cameraTrackRef.current = cameraTrack;

    const audioTracks = localStreamRef.current?.getAudioTracks().filter(isLive) ?? [];
    const nextStream = new MediaStream([...audioTracks, cameraTrack]);
    setLocalMediaStream(nextStream);
    setVideoEnabledValue(true);
    replaceVideoTrackForPeers(cameraTrack);
    broadcastMediaState();
    return cameraTrack;
  }, [broadcastMediaState, replaceVideoTrackForPeers, setLocalMediaStream, setVideoEnabledValue]);

  const createOfferForPeer = useCallback(
    async (peerId: string, offerMode: CallMode) => {
      const connection = createPeerConnection(peerId);
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);

      if (!connection.localDescription) return;

      sendSignal('offer', {
        ...buildBaseSignal(),
        to: peerId,
        mode: offerMode,
        sdp: connection.localDescription.toJSON(),
      } satisfies SessionSignal);
    },
    [buildBaseSignal, createPeerConnection, sendSignal],
  );

  const handleJoinCall = useCallback(
    async (payload: JoinSignal) => {
      if (!payload?.from || payload.from === profileRef.current.id) return;

      updateParticipantMeta(payload.from, {
        username: payload.username,
        displayName: payload.displayName,
        avatarUrl: payload.avatarUrl,
        audioEnabled: payload.audioEnabled,
        videoEnabled: payload.videoEnabled,
        screenSharing: payload.screenSharing,
      });

      if (!activeRef.current || !localStreamRef.current) {
        setIncomingCall({
          id: payload.callId,
          from: payload.from,
          displayName: payload.displayName,
          username: payload.username,
          avatarUrl: payload.avatarUrl,
          mode: payload.mode,
        });
        setStatus(`${payload.displayName} started a ${payload.mode} call`);
        return;
      }

      try {
        await createOfferForPeer(payload.from, modeRef.current ?? payload.mode);
      } catch (callError) {
        setError(callError instanceof Error ? callError.message : 'Could not create the call offer.');
      }
    },
    [createOfferForPeer, updateParticipantMeta],
  );

  const handleOffer = useCallback(
    async (payload: SessionSignal) => {
      if (payload?.to !== profileRef.current.id || payload.from === profileRef.current.id) return;

      updateParticipantMeta(payload.from, {
        username: payload.username,
        displayName: payload.displayName,
        avatarUrl: payload.avatarUrl,
        audioEnabled: payload.audioEnabled,
        videoEnabled: payload.videoEnabled,
        screenSharing: payload.screenSharing,
      });

      if (!activeRef.current) {
        setIncomingCall({
          id: callIdRef.current ?? createSignalId('incoming'),
          from: payload.from,
          displayName: payload.displayName,
          username: payload.username,
          avatarUrl: payload.avatarUrl,
          mode: payload.mode,
        });
        return;
      }

      try {
        await ensureLocalStream(modeRef.current ?? payload.mode);
        const connection = createPeerConnection(payload.from);

        if (connection.signalingState !== 'stable') {
          try {
            await connection.setLocalDescription({ type: 'rollback' });
          } catch {
            // Some browsers reject rollback when the peer is already stable enough to continue.
          }
        }

        await connection.setRemoteDescription(new RTCSessionDescription(payload.sdp));

        const answer = await connection.createAnswer();
        await connection.setLocalDescription(answer);

        if (!connection.localDescription) return;

        sendSignal('answer', {
          ...buildBaseSignal(),
          to: payload.from,
          mode: modeRef.current ?? payload.mode,
          sdp: connection.localDescription.toJSON(),
        } satisfies SessionSignal);
      } catch (callError) {
        setError(callError instanceof Error ? callError.message : 'Could not answer the call.');
      }
    },
    [buildBaseSignal, createPeerConnection, ensureLocalStream, sendSignal, updateParticipantMeta],
  );

  const handleAnswer = useCallback(async (payload: SessionSignal) => {
    if (payload?.to !== profileRef.current.id || payload.from === profileRef.current.id) return;

    updateParticipantMeta(payload.from, {
      username: payload.username,
      displayName: payload.displayName,
      avatarUrl: payload.avatarUrl,
      audioEnabled: payload.audioEnabled,
      videoEnabled: payload.videoEnabled,
      screenSharing: payload.screenSharing,
    });

    const connection = peerConnectionsRef.current.get(payload.from);
    if (!connection) return;

    try {
      if (connection.signalingState !== 'stable') {
        await connection.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      }
    } catch (callError) {
      setError(callError instanceof Error ? callError.message : 'Could not connect the call answer.');
    }
  }, [updateParticipantMeta]);

  const handleIceCandidate = useCallback(async (payload: IceSignal) => {
    if (payload?.to !== profileRef.current.id || payload.from === profileRef.current.id) return;

    const connection = peerConnectionsRef.current.get(payload.from);
    if (!connection) return;

    try {
      await connection.addIceCandidate(new RTCIceCandidate(payload.candidate));
    } catch (callError) {
      setError(callError instanceof Error ? callError.message : 'Could not add the call network candidate.');
    }
  }, []);

  const handleLeaveCall = useCallback(
    (payload: LeaveSignal) => {
      if (!payload?.from || payload.from === profileRef.current.id) return;
      closePeer(payload.from);
      setIncomingCall((current) => (current?.from === payload.from ? null : current));
      setStatus('A participant left the call');
    },
    [closePeer],
  );

  const handleMediaState = useCallback(
    (payload: BaseSignal) => {
      if (!payload?.from || payload.from === profileRef.current.id) return;

      updateParticipantMeta(payload.from, {
        username: payload.username,
        displayName: payload.displayName,
        avatarUrl: payload.avatarUrl,
        audioEnabled: payload.audioEnabled,
        videoEnabled: payload.videoEnabled,
        screenSharing: payload.screenSharing,
      });
    },
    [updateParticipantMeta],
  );

  const pushReaction = useCallback((reaction: CallReaction) => {
    setReactions((current) => [...current.slice(-5), reaction]);
    window.setTimeout(() => {
      setReactions((current) => current.filter((item) => item.id !== reaction.id));
    }, 3600);
  }, []);

  const handleReaction = useCallback(
    (payload: ReactionSignal) => {
      if (!payload?.id || payload.from === profileRef.current.id) return;
      pushReaction(payload);
    },
    [pushReaction],
  );

  const leaveCall = useCallback(() => {
    if (activeRef.current) {
      sendSignal('leave-call', { from: profileRef.current.id } satisfies LeaveSignal);
    }

    peerConnectionsRef.current.forEach((connection) => connection.close());
    peerConnectionsRef.current.clear();
    videoSendersRef.current.clear();
    remoteStreamsRef.current.clear();
    participantMetaRef.current.clear();
    publishRemoteParticipants();
    stopAllLocalMedia();
    callIdRef.current = null;
    setActiveValue(false);
    setModeValue(null);
    setAudioEnabledValue(true);
    setVideoEnabledValue(false);
    setScreenSharingValue(false);
    setIncomingCall(null);
    setStatus('Call ended');
  }, [
    publishRemoteParticipants,
    sendSignal,
    setActiveValue,
    setAudioEnabledValue,
    setModeValue,
    setScreenSharingValue,
    setVideoEnabledValue,
    stopAllLocalMedia,
  ]);

  const startCall = useCallback(
    async (nextMode: CallMode) => {
      setError(null);

      if (!conversationId) {
        setError('Select a conversation before starting a call.');
        return;
      }

      try {
        if (activeRef.current) {
          if (nextMode === 'video') {
            await enableCameraTrack();
            setModeValue('video');
          }
          broadcastMediaState();
          return;
        }

        setAudioEnabledValue(true);
        setVideoEnabledValue(nextMode === 'video');
        setScreenSharingValue(false);
        const stream = await ensureLocalStream(nextMode);
        setLocalMediaStream(stream);
        callIdRef.current = createSignalId(`call-${conversationId}`);
        setModeValue(nextMode);
        setActiveValue(true);
        setIncomingCall(null);
        setStatus(`${nextMode === 'video' ? 'Video' : 'Voice'} call started`);

        sendSignal('join-call', {
          ...buildBaseSignal(),
          callId: callIdRef.current,
          mode: nextMode,
        } satisfies JoinSignal);
      } catch (callError) {
        setError(callError instanceof Error ? callError.message : 'Could not start the call.');
        setActiveValue(false);
      }
    },
    [
      broadcastMediaState,
      buildBaseSignal,
      conversationId,
      enableCameraTrack,
      ensureLocalStream,
      sendSignal,
      setActiveValue,
      setAudioEnabledValue,
      setLocalMediaStream,
      setModeValue,
      setScreenSharingValue,
      setVideoEnabledValue,
    ],
  );

  const joinIncomingCall = useCallback(async () => {
    if (!incomingCall) return;
    await startCall(incomingCall.mode);
  }, [incomingCall, startCall]);

  const toggleMute = useCallback(() => {
    const nextValue = !audioEnabledRef.current;
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = nextValue;
    });
    setAudioEnabledValue(nextValue);
    setStatus(nextValue ? 'Microphone on' : 'Microphone muted');
    broadcastMediaState();
  }, [broadcastMediaState, setAudioEnabledValue]);

  const toggleCamera = useCallback(async () => {
    setError(null);

    if (screenSharingRef.current) {
      setError('Stop screen sharing before switching your camera.');
      return;
    }

    try {
      const nextValue = !videoEnabledRef.current;

      if (nextValue) {
        await enableCameraTrack();
        setModeValue('video');
        setStatus('Camera on');
        return;
      }

      localStreamRef.current?.getVideoTracks().forEach((track) => {
        track.enabled = false;
      });
      setVideoEnabledValue(false);
      replaceVideoTrackForPeers(null);
      setStatus('Camera off');
      broadcastMediaState();
    } catch (callError) {
      setError(callError instanceof Error ? callError.message : 'Could not switch the camera.');
    }
  }, [broadcastMediaState, enableCameraTrack, replaceVideoTrackForPeers, setModeValue, setVideoEnabledValue]);

  const startScreenShare = useCallback(async () => {
    setError(null);

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError('Screen sharing is not supported by this browser.');
      return;
    }

    if (!activeRef.current) {
      await startCall('voice');
      if (!activeRef.current) return;
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const [screenTrack] = screenStream.getVideoTracks();

      if (!screenTrack) {
        throw new Error('No screen track was returned by the browser.');
      }

      const currentCameraTrack = localStreamRef.current?.getVideoTracks().find(isLive);
      if (currentCameraTrack && !screenSharingRef.current) {
        cameraTrackRef.current = currentCameraTrack;
      }

      screenTrack.onended = stopScreenShare;
      screenStreamRef.current = screenStream;

      const audioTracks = localStreamRef.current?.getAudioTracks().filter(isLive) ?? [];
      const nextStream = new MediaStream([...audioTracks, screenTrack]);
      setLocalMediaStream(nextStream);
      replaceVideoTrackForPeers(screenTrack);
      setScreenSharingValue(true);
      setVideoEnabledValue(true);
      setModeValue('video');
      setStatus('Screen sharing');
      broadcastMediaState();
    } catch (callError) {
      setError(callError instanceof Error ? callError.message : 'Could not start screen sharing.');
    }
  }, [
    broadcastMediaState,
    replaceVideoTrackForPeers,
    setLocalMediaStream,
    setModeValue,
    setScreenSharingValue,
    setVideoEnabledValue,
    startCall,
    stopScreenShare,
  ]);

  const sendReaction = useCallback(
    (emoji: string) => {
      const reaction: CallReaction = {
        id: createSignalId('reaction'),
        emoji,
        from: profileRef.current.id,
        displayName: profileRef.current.display_name,
      };

      pushReaction(reaction);
      sendSignal('call-reaction', reaction satisfies ReactionSignal);
    },
    [pushReaction, sendSignal],
  );

  useEffect(() => {
    if (!conversationId) {
      return undefined;
    }

    const client = getSupabase();
    const channel = client.channel(`relay-call:${conversationId}`, {
      config: {
        broadcast: {
          self: false,
        },
      },
    });

    channelRef.current = channel;

    channel
      .on('broadcast', { event: 'join-call' }, ({ payload }) => void handleJoinCall(payload as JoinSignal))
      .on('broadcast', { event: 'offer' }, ({ payload }) => void handleOffer(payload as SessionSignal))
      .on('broadcast', { event: 'answer' }, ({ payload }) => void handleAnswer(payload as SessionSignal))
      .on('broadcast', { event: 'ice-candidate' }, ({ payload }) => void handleIceCandidate(payload as IceSignal))
      .on('broadcast', { event: 'leave-call' }, ({ payload }) => handleLeaveCall(payload as LeaveSignal))
      .on('broadcast', { event: 'media-state' }, ({ payload }) => handleMediaState(payload as BaseSignal))
      .on('broadcast', { event: 'call-reaction' }, ({ payload }) => handleReaction(payload as ReactionSignal))
      .subscribe((subscriptionStatus) => {
        if (subscriptionStatus === 'SUBSCRIBED' && !activeRef.current) {
          setStatus('Call channel ready');
        }

        if (subscriptionStatus === 'CHANNEL_ERROR') {
          setError('Realtime call channel could not connect.');
        }
      });

    return () => {
      leaveCall();
      void client.removeChannel(channel);
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [
    conversationId,
    handleAnswer,
    handleIceCandidate,
    handleJoinCall,
    handleLeaveCall,
    handleMediaState,
    handleOffer,
    handleReaction,
    leaveCall,
  ]);

  return {
    active,
    mode,
    status,
    error,
    incomingCall,
    localStream,
    remoteParticipants,
    audioEnabled,
    videoEnabled,
    screenSharing,
    reactions,
    startCall,
    joinIncomingCall,
    leaveCall,
    toggleMute,
    toggleCamera,
    startScreenShare,
    stopScreenShare,
    sendReaction,
    dismissIncomingCall: () => setIncomingCall(null),
    clearError: () => setError(null),
  };
}
